import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { LayoutService } from '../../../services/layout.service';
import { DragContextService } from '../../../services/dragcontext.service';
import { PlaceholderService } from '../../../services/placeholder.service';
import { FloatingShelfProduct } from '../../../models/layout/floating-shelf-product';
import { ProductReference } from '../../../models/layout/product-reference';
import { ImageComponent } from '../../../image/image.component';
import { FloatingShelfCapacityModalComponent } from '../../../modals/floating-shelf-capacity-modal/floating-shelf-capacity-modal.component';
import { CapabilitiesService } from '../../../services/capabilities.service';
import { CapabilityType } from '../../../models/capability-type';

/**
 * Horizontal tray pinned just above the footer on the planogram page. Users drag
 * products from the product bin onto it; each product appears once (de-duplicated
 * by product id) with a configurable capacity and a remove action. The contents
 * are part of the planogram layout and are persisted with the saved state.
 */
@Component({
  selector: 'app-floating-shelf',
  templateUrl: './floating-shelf.component.html',
  styleUrls: ['./floating-shelf.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ImageComponent],
})
export class FloatingShelfComponent implements OnInit, OnDestroy {
  items: FloatingShelfProduct[] = [];
  productReference: Map<number, ProductReference> = new Map();
  isDropTarget = false;

  private destroy$ = new Subject<void>();

  constructor(
    private layoutService: LayoutService,
    private dragContextService: DragContextService,
    private placeholderService: PlaceholderService,
    private modalService: NgbModal,
    private capabilitiesService: CapabilitiesService,
    private cdr: ChangeDetectorRef
  ) {}

  // The floating shelf is editable only when the planogram is (false for linked/view-only
  // planograms and VIEW shares) — gates the drop target plus the configure/remove actions.
  get canEdit(): boolean {
    return this.capabilitiesService.hasCapability(CapabilityType.EDIT_PLANOGRAM);
  }

  ngOnInit(): void {
    this.layoutService.layout$
      .pipe(takeUntil(this.destroy$))
      .subscribe((layout) => {
        this.items = layout?.floatingShelf ?? [];
        this.productReference = layout?.productReference ?? new Map();
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getProduct(productId: number): ProductReference | undefined {
    return this.productReference.get(productId);
  }

  /**
   * Fires on mouse move over the tray. While a product is being dragged from the
   * product bin (and isn't already present) the tray becomes the active drop
   * target so the global drop handler adds it on release.
   */
  onDragOver(): void {
    if (!this.canEdit) return;
    if (!this.dragContextService.isDraggingProduct()) {
      // Drag finished (e.g. the product was just dropped onto the tray, where
      // mouseup fires no mouseleave) — drop the active highlight.
      if (this.isDropTarget) {
        this.isDropTarget = false;
        this.cdr.markForCheck();
      }
      return;
    }

    const context = this.dragContextService.getContext();
    if (context.source?.type !== 'bin') return;

    const product = context.draggedProduct;
    if (!product) return;

    if (this.items.some((p) => p.productId === product.productId)) {
      // Already on the floating shelf — not a valid drop target.
      if (this.isDropTarget) {
        this.isDropTarget = false;
        this.placeholderService.clearPlaceholder();
        this.cdr.markForCheck();
      }
      return;
    }

    this.placeholderService.setFloatingShelfPlaceholder(product);
    if (!this.isDropTarget) {
      this.isDropTarget = true;
      this.cdr.markForCheck();
    }
  }

  onDragLeave(): void {
    if (!this.isDropTarget) return;
    this.isDropTarget = false;
    this.placeholderService.clearPlaceholder();
    this.cdr.markForCheck();
  }

  configure(item: FloatingShelfProduct): void {
    if (!this.canEdit) return;
    const ref = this.getProduct(item.productId);
    const modalRef = this.modalService.open(
      FloatingShelfCapacityModalComponent,
      { centered: true }
    );
    modalRef.componentInstance.productName =
      ref?.description ?? `Product ${item.productId}`;
    modalRef.componentInstance.capacity = item.capacity;
    modalRef.result.then(
      (capacity: number | undefined) => {
        if (capacity != null) {
          this.layoutService.setFloatingShelfCapacity(item.productId, capacity);
        }
      },
      () => {
        /* dismissed — no change */
      }
    );
  }

  remove(item: FloatingShelfProduct): void {
    if (!this.canEdit) return;
    this.layoutService.removeProductFromFloatingShelf(item.productId);
  }
}
